/* Purchase adapters only. Elevate product-form remains the sole cart submitter. */
(() => {
  const readVariants = element => {
    try { return JSON.parse(element.querySelector('[data-meso-variant-data]')?.textContent || '{}'); }
    catch { return {}; }
  };
  const setQuantityRules = (element, variant) => {
    const input = element.querySelector('input[name="quantity"]');
    if (!input || !variant) return;
    input.min = variant.min;
    input.step = variant.step;
    if (variant.max != null) input.max = variant.max; else input.removeAttribute('max');
    const raw = Number(input.value) || variant.min;
    let quantity = variant.min + Math.floor(Math.max(0, raw - variant.min) / variant.step) * variant.step;
    if (variant.max != null) quantity = Math.min(quantity, variant.max);
    input.value = quantity;
    input.dispatchEvent(new Event('change', {bubbles:true}));
  };
  class MesoProductController extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected && !this.ready) this.init(); });
    }
    init() {
      this.form = this.querySelector('product-form form');
      this.button = this.form?.querySelector('[name="add"]');
      if (!this.form || !this.button) return;
      this.ready = true;
      this.variants = readVariants(this);
      this.abort = new AbortController();
      this.addEventListener('change', event => {
        if (event.target.matches('input[name="id"]')) this.updateVariant();
      }, {signal:this.abort.signal});
      this.sticky = this.querySelector('[data-meso-sticky]');
      this.stickyButton = this.querySelector('[data-meso-sticky-submit]');
      this.stickyButton?.addEventListener('click', () => {
        if (!this.button.disabled && this.button.getAttribute('aria-disabled') !== 'true') this.form.requestSubmit(this.button);
      }, {signal:this.abort.signal});
      if (this.sticky && !window.Shopify?.designMode && !this.closest('quick-add-modal')) {
        this.visibility = new IntersectionObserver(entries => {
          const entry = entries[0];
          this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
        });
        this.visibility.observe(this.button);
        this.buttonObserver = new MutationObserver(() => this.syncSticky());
        this.buttonObserver.observe(this.button, {attributes:true, attributeFilter:['disabled','aria-disabled','class'], childList:true, subtree:true, characterData:true});
      }
      // Native variant renderer owns price/availability; our listener mirrors its final state only.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          const picker = this.querySelector('variant-selects');
          if (String(event.data?.sectionId) === String(picker?.dataset.originalSection || this.dataset.section)) this.updateVariant();
        });
      }
      this.updateVariant();
    }
    updateVariant() {
      const id = this.form.querySelector('[name="id"]').value;
      const variant = this.variants[id];
      if (!variant) return;
      const helper = this.querySelector('[data-meso-helper]');
      if (helper) helper.textContent = variant.helper;
      const details = this.querySelector('[data-meso-details]');
      if (details) { const url = new URL(details.href); url.searchParams.set('variant', id); details.href = url.href; }
      const label = this.querySelector('[data-meso-sticky-variant]');
      if (label) label.textContent = variant.title === 'Default Title' ? '' : variant.title;
      const price = this.querySelector('[data-meso-sticky-price]');
      if (price) price.textContent = variant.price;
      if (!this.closest('quick-add-modal')) document.querySelectorAll('[data-meso-current-product]').forEach(node => {
        if (node.dataset.mesoCurrentProduct === this.dataset.productId) node.textContent = variant.title === 'Default Title' ? '' : variant.title;
      });
      if (this.selectedId !== id) { this.selectedId = id; setQuantityRules(this, variant); }
      this.syncSticky();
    }
    syncSticky() {
      if (!this.stickyButton) return;
      this.stickyButton.disabled = this.button.disabled || this.button.getAttribute('aria-disabled') === 'true';
      this.stickyButton.textContent = this.button.querySelector('.main-atc__label__text')?.textContent || this.button.textContent.trim();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.visibility?.disconnect(); this.buttonObserver?.disconnect(); this.unsubscribe?.(); this.ready = false;
    }
  }
  class MesoCompanion extends HTMLElement {
    connectedCallback() { queueMicrotask(() => { if (this.isConnected && !this.ready) this.init(); }); }
    init() {
      this.select = this.querySelector('[data-meso-companion-select]');
      this.form = this.querySelector('product-form form');
      if (!this.select || !this.form) return;
      this.ready = true;
      this.variants = readVariants(this);
      this.originalImage = this.querySelector('img')?.src;
      this.abort = new AbortController();
      this.select.addEventListener('change', () => this.updateVariant(), {signal:this.abort.signal});
      this.updateVariant();
    }
    updateVariant() {
      const variant = this.variants[this.select.value];
      if (!variant) return;
      const input = this.form.querySelector('[name="id"]');
      input.value = this.select.value;
      input.dispatchEvent(new Event('change', {bubbles:true}));
      this.querySelector('[data-meso-companion-price]').textContent = variant.price;
      const button = this.form.querySelector('[name="add"]');
      button.disabled = !variant.available;
      button.dataset.unavailable = String(!variant.available);
      button.querySelector('.main-atc__label__text').textContent = variant.available ? (window.variantStrings?.addToCart || 'Add to cart') : (window.variantStrings?.soldOut || 'Sold out');
      const image = this.querySelector('img');
      if (image) { image.removeAttribute('srcset'); image.src = variant.image || this.originalImage; }
      setQuantityRules(this, variant);
    }
    disconnectedCallback() { this.abort?.abort(); this.ready = false; }
  }
  // Read-only information adapter. Native VariantSelects and product-form own commerce.
  class MesoProductInfo extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected && !this.ready) this.init(); });
    }
    init() {
      try { this.map = JSON.parse(this.querySelector('[data-meso-info-map]').textContent); }
      catch { return; }
      this.ready = true;
      this.abort = new AbortController();
      const belongs = controller => controller && !controller.closest('quick-add-modal') && controller.dataset.productId === this.dataset.productId;
      document.addEventListener('change', event => {
        const controller = event.target.closest('meso-product-controller');
        if (!belongs(controller)) return;
        if (event.target.matches('input[name="id"]')) this.update(event.target.value);
        else if (event.target.closest('variant-selects')) {
          // Invalid option combinations have no currentVariant: clear stale flavour data.
          this.update(event.target.closest('variant-selects').currentVariant?.id);
        }
      }, {signal:this.abort.signal});
      const controller = [...document.querySelectorAll('meso-product-controller')].find(belongs);
      if (controller) this.update(controller.querySelector('product-form input[name="id"]')?.value);
    }
    update(id) {
      const profile = this.map[String(id)] || '';
      if (this.activeProfile === profile) return;
      this.activeProfile = profile;
      const templates = [...this.querySelectorAll('template[data-meso-info-profile]')];
      this.querySelectorAll('[data-meso-info-row]').forEach(row => {
        const source = templates.find(item => item.dataset.mesoInfoProfile === profile && item.dataset.field === row.dataset.mesoInfoRow);
        const body = row.querySelector('[data-meso-info-body]');
        body.replaceChildren(...(source ? [source.content.cloneNode(true)] : []));
        row.hidden = !body.textContent.trim();
      });
    }
    disconnectedCallback() { this.abort?.abort(); this.ready = false; this.activeProfile = undefined; }
  }
  if (!customElements.get('meso-product-info')) customElements.define('meso-product-info', MesoProductInfo);
  if (!customElements.get('meso-product-controller')) customElements.define('meso-product-controller', MesoProductController);
  if (!customElements.get('meso-companion')) customElements.define('meso-companion', MesoCompanion);
})();
